import {
  adminHttp as http,
  adminSdk as admin,
  makeApiUrl,
} from "@shared/lib/http";
import { getAccessToken, getTenantId } from "@shared/lib/auth";
import { isStubEnabled } from "@sdk/core";
import { toCSV } from "@shared/lib/csv";
import { hasUpToDecimalPlaces, isWholeNumber } from "@shared/lib/numeric";
import { SLUG_REGEX } from "@shared/lib/slug";
import type { VariantRow } from "@shared/lib/variantMatrix";
import { parseApiError } from "@shared/lib/errors";
import { translateUi } from "@i18n/config";
import {
  ensureId,
  sanitizeString,
  isValidGuid,
  mapVariantOptions,
  mapImageToApi,
  normalizeProduct,
  parseDownloadFileName,
  toRow,
} from "./products.helpers";
import { listProductsFromStub, loadLocal, persistLocal } from "./products.stub";

export type ProductStatus = "active" | "inactive";

export type ProductImage = {
  id: string;
  url: string;
  alt?: string;
  sort: number;
  isPrimary?: boolean;
};

export type ProductRow = {
  id: string;
  rowVersion?: string | null;
  name: string;
  price?: number | null;
  priceMin: number;
  priceMax: number;
  status: ProductStatus;
  activateAtUtc?: string | null;
  storefrontSortOrder?: number;
  createdAt: string;
  updatedAt: string;
  imageUrl?: string;
  slug?: string;
  salePrice?: number | null;
  compareAt?: number | null;
  compareAtPrice?: number | null;
  compareAtMax?: number | null;
  stockQty?: number;
  trackInventory?: boolean;
  currency?: string;
  brandId?: string | null;
  brandName?: string | null;
  brandSlug?: string | null;
  categoryIds?: string[];
  sku?: string;
  translations?: { [key: string]: ProductTranslation };
  publicUrl?: string;
  productType?: number;
  categoryCount?: number;
  weight?: number | null;
};

export type ProductTranslation = {
  title?: string;
  description?: string;
  shortDescription?: string;
  metaTitle?: string;
  metaDescription?: string;
};

export const CATALOG_TRANSLATION_LOCALES = ["en", "ar", "tr"] as const;
export const PRODUCT_TRANSLATION_FIELDS = [
  "title",
  "description",
  "shortDescription",
  "metaTitle",
  "metaDescription",
] as const;
export const productTranslationColumn = (locale: string, field: string) =>
  `translations.${locale}.${field}`;

export type ProductDetail = ProductRow & {
  description: string;
  shortDescription?: string;
  sku: string;
  price: number;
  salePrice?: number | null;
  currency: string;
  trackInventory: boolean;
  stockQty: number;
  brandId?: string | null;
  brandName?: string | null;
  brandSlug?: string | null;
  categoryIds: string[];
  labelIds: string[];
  metaTitle?: string;
  metaDescription?: string;
  images: ProductImage[];
  variants: VariantRow[];
  translations?: { [key: string]: ProductTranslation };
  publicUrl?: string;
  productType: number;
  categoryCount: number;
  weight: number | null;
};

export type ProductInput = Omit<
  ProductDetail,
  "id" | "createdAt" | "updatedAt" | "priceMin" | "priceMax" | "status"
> & {
  status: ProductStatus;
};

export type ProductTranslationResult = {
  productId: string;
  sourceLanguage: string;
  translations: Record<string, ProductTranslation>;
  provider: string;
};

export type ProductTranslationEstimate = {
  totalProducts: number;
  productsWithMissingFields: number;
  productsAlreadyComplete: number;
  productsWithoutSource: number;
  existingTargetTranslations: number;
  missingFields: number;
  estimatedProviderCalls: number;
};

export type ProductTranslationJobItemResult = {
  productId: string;
  productTitle: string;
  status: string;
  detectedSourceLanguage?: string | null;
  attemptCount: number;
  errorMessage?: string | null;
};

export type ProductTranslationJob = {
  id: string;
  mode: "Full" | "Missing";
  sourceLanguage: string;
  targetLanguages: string[];
  status:
    | "Queued"
    | "Running"
    | "Completed"
    | "CompletedWithErrors"
    | "Failed"
    | "Canceled";
  totalProducts: number;
  processedProducts: number;
  translatedProducts: number;
  skippedProducts: number;
  failedProducts: number;
  cancelRequested: boolean;
  errorMessage?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
  failedItems: ProductTranslationJobItemResult[];
};

export type ListParams = {
  q?: string;
  status?: ProductStatus | "scheduled" | "";
  categoryId?: string;
  brandId?: string;
  stock?: "in" | "out" | "low" | "";
  page?: number;
  pageSize?: number;
  sort?: "name" | "price" | "updatedAt" | "createdAt" | "storefront";
  sortDir?: "asc" | "desc";
};

export type ListResponse = {
  items: ProductRow[];
  total: number;
  page: number;
  pageSize: number;
};

export type BulkEditField =
  | "price"
  | "salePrice"
  | "stockQuantity"
  | "trackInventory"
  | "weight"
  | "brandId"
  | "categoryIds"
  | "labelIds"
  | "status"
  | "activateAtUtc";

export type BulkEditProductSnapshot = {
  id: string;
  title: string;
  status: ProductStatus;
  isVariable: boolean;
  price: number;
  salePrice: number | null;
  stockQuantity: number;
  trackInventory: boolean;
  weight: number | null;
  brandId: string | null;
  categoryIds: string[];
  labelIds: string[];
  rowVersion: string | null;
  activateAtUtc?: string | null;
};

export type BulkEditSnapshotResponse = {
  items: BulkEditProductSnapshot[];
  missingProductIds: string[];
};

export type BulkEditProductPatch = {
  productId: string;
  rowVersion?: string | null;
  changedFields: BulkEditField[];
  status?: ProductStatus;
  activateAtUtc?: string | null;
  price?: number | null;
  salePrice?: number | null;
  stockQuantity?: number | null;
  trackInventory?: boolean | null;
  weight?: number | null;
  brandId?: string | null;
  categoryIds?: string[] | null;
  labelIds?: string[] | null;
};

export type BulkEditProductResultItem = {
  productId: string;
  outcome: "updated" | "failed" | "skipped";
  code?: string | null;
  message?: string | null;
  status?: ProductStatus | null;
};

export type BulkEditProductsResult = {
  requestedCount: number;
  updatedCount: number;
  failedCount: number;
  skippedCount: number;
  items: BulkEditProductResultItem[];
};

const stubMode = () => isStubEnabled();
const PRODUCT_LOAD_RETRY_ATTEMPTS = 3;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function rawProductId(raw: any): string {
  return String(raw?.id ?? raw?.Id ?? "").trim();
}

function hasCompleteProductCreatePayload(raw: any): boolean {
  return Boolean(
    rawProductId(raw) &&
    String(raw?.name ?? raw?.title ?? raw?.Name ?? raw?.Title ?? "").trim() &&
    String(raw?.slug ?? raw?.Slug ?? "").trim() &&
    (raw?.createdAt ??
      raw?.createdAtUtc ??
      raw?.CreatedAt ??
      raw?.CreatedAtUtc) &&
    (raw?.updatedAt ??
      raw?.updatedAtUtc ??
      raw?.UpdatedAt ??
      raw?.UpdatedAtUtc) &&
    (raw?.status !== undefined ||
      raw?.Status !== undefined ||
      raw?.isActive !== undefined ||
      raw?.IsActive !== undefined),
  );
}

function isRetryableLoadError(err: unknown) {
  const parsed = parseApiError(err);
  return parsed.status !== 404 && parsed.retryable !== false;
}

function assertMoneyValue(value: number | null | undefined, label: string) {
  if (value === null || value === undefined) return;
  if (!Number.isFinite(value))
    throw new Error(
      translateUi(
        "value0MustBeANumberUsingUp",
        "{{value0}} must be a number, using up to 2 decimal places",
        { value0: label },
      ),
    );
  if (value < 0)
    throw new Error(
      translateUi("value0MustBe0OrGreater", "{{value0}} must be 0 or greater", {
        value0: label,
      }),
    );
  if (!hasUpToDecimalPlaces(value, 2))
    throw new Error(
      translateUi(
        "value0MustUseUpTo2Decimal",
        "{{value0}} must use up to 2 decimal places",
        { value0: label },
      ),
    );
}

function assertStockValue(
  value: number | null | undefined,
  label: string,
  required: boolean,
) {
  if (value === null || value === undefined) {
    if (required)
      throw new Error(
        translateUi(
          "value0IsRequiredLowercaseInterpolated",
          "{{value0}} is required",
          {
            value0: label,
          },
        ),
      );
    return;
  }
  if (!isWholeNumber(value) || value < 0)
    throw new Error(
      translateUi(
        "value0MustBeAWholeNumber0",
        "{{value0}} must be a whole number 0 or greater",
        { value0: label },
      ),
    );
}

function assertProductNumericPayload(
  payload: Pick<
    Partial<ProductInput>,
    "price" | "salePrice" | "stockQty" | "trackInventory" | "variants"
  >,
  options: { requirePrice?: boolean; requireStock?: boolean } = {},
) {
  if (
    options.requirePrice &&
    (payload.price === null || payload.price === undefined)
  ) {
    throw new Error(translateUi("priceIsRequired", "Price is required"));
  }
  assertMoneyValue(payload.price, translateUi("priceTitleCase", "Price"));
  assertMoneyValue(
    payload.salePrice,
    translateUi("salePriceSentenceCase", "Sale price"),
  );
  if (
    payload.price !== null &&
    payload.price !== undefined &&
    payload.salePrice !== null &&
    payload.salePrice !== undefined &&
    payload.salePrice > payload.price
  ) {
    throw new Error(
      translateUi(
        "salePriceMustBeLessThanOrEqualToTheProduct",
        "Sale price must be less than or equal to the product price",
      ),
    );
  }
  assertStockValue(
    payload.stockQty,
    translateUi("stockQuantitySentenceCase", "Stock quantity"),
    options.requireStock ?? payload.trackInventory === true,
  );

  payload.variants?.forEach((variant, index) => {
    const variantLabel = variant.sku?.trim()
      ? translateUi("variantSkuTitleCaseInterpolated", "Variant {{sku}}", {
          sku: variant.sku,
        })
      : translateUi("variantNumber", "Variant {{number}}", {
          number: index + 1,
        });
    assertMoneyValue(
      variant.price,
      translateUi("variantPrice", "{{variant}} price", {
        variant: variantLabel,
      }),
    );
    assertMoneyValue(
      variant.compareAt,
      translateUi("variantSalePrice", "{{variant}} sale price", {
        variant: variantLabel,
      }),
    );
    if (
      variant.price !== null &&
      variant.price !== undefined &&
      variant.compareAt !== null &&
      variant.compareAt !== undefined &&
      variant.compareAt > variant.price
    ) {
      throw new Error(
        translateUi(
          "value0SalePriceMustBeLessThan",
          "{{value0}} sale price must be less than or equal to the variant price",
          { value0: variantLabel },
        ),
      );
    }
    assertStockValue(
      variant.stockQty,
      translateUi("variantStockQuantity", "{{variant}} stock quantity", {
        variant: variantLabel,
      }),
      variant.trackInventory !== false,
    );
    assertMoneyValue(
      variant.weight,
      translateUi("variantWeight", "{{variant}} weight", {
        variant: variantLabel,
      }),
    );
  });
}

function toApiProductMutationPayload(patch: Partial<ProductInput>) {
  const apiPatch: any = {};
  if (patch.rowVersion != null) apiPatch.RowVersion = patch.rowVersion;
  if (patch.name !== undefined) apiPatch.Title = sanitizeString(patch.name);
  if (patch.description !== undefined)
    apiPatch.Description = sanitizeString(patch.description);
  if (patch.shortDescription !== undefined)
    apiPatch.ShortDescription = sanitizeString(patch.shortDescription);
  if (patch.slug !== undefined) apiPatch.Slug = sanitizeString(patch.slug);
  if (patch.sku !== undefined) apiPatch.Sku = patch.sku;
  if (patch.price !== undefined) apiPatch.Price = patch.price;
  if (patch.salePrice !== undefined) {
    apiPatch.SalePrice = patch.salePrice;
    apiPatch.ClearSalePrice = patch.salePrice === null;
  }
  if (patch.currency !== undefined) {
    apiPatch.DefaultCurrency = patch.currency;
  }
  if (patch.status !== undefined) apiPatch.IsActive = patch.status === "active";
  if (patch.activateAtUtc !== undefined) {
    apiPatch.ActivateAtUtc = patch.activateAtUtc || null;
    apiPatch.ClearActivation = !patch.activateAtUtc;
  }
  if (patch.trackInventory !== undefined)
    apiPatch.TrackInventory = patch.trackInventory;
  if (patch.stockQty !== undefined) apiPatch.StockQuantity = patch.stockQty;
  if (patch.brandId !== undefined) {
    apiPatch.BrandId = patch.brandId;
    apiPatch.ClearBrand = patch.brandId === null;
  }
  if (patch.categoryIds !== undefined) apiPatch.CategoryIds = patch.categoryIds;
  if (patch.labelIds !== undefined) apiPatch.LabelIds = patch.labelIds;
  if (patch.metaTitle !== undefined)
    apiPatch.MetaTitle = sanitizeString(patch.metaTitle);
  if (patch.metaDescription !== undefined)
    apiPatch.MetaDescription = sanitizeString(patch.metaDescription);
  if (patch.weight !== undefined) {
    apiPatch.Weight = patch.weight;
    apiPatch.ClearWeight = patch.weight === null;
  }
  if (patch.images !== undefined)
    apiPatch.Images = patch.images.map((img, idx) => mapImageToApi(img, idx));
  if (patch.variants !== undefined) {
    const variantCurrency = patch.currency || "USD";
    apiPatch.Variants = patch.variants.map((v) => {
      const stockQuantity = v.stockQty ?? v.stockQuantity ?? 0;
      return {
        Sku: v.sku,
        Barcode: v.barcode,
        Price: v.price,
        Weight: v.weight,
        CompareAtPrice: v.compareAt,
        InheritsBasePrice: v.inheritsBasePrice ?? false,
        Currency: v.currency || variantCurrency,
        StockQuantity: stockQuantity,
        stockQuantity,
        TrackInventory: v.trackInventory ?? true,
        IsDefault: v.isDefault ?? false,
        IsActive: v.isActive ?? true,
        SortOrder: v.sortOrder ?? 0,
        Options: mapVariantOptions(v.options),
        TierPrices: v.tierPrices || {},
        Id: isValidGuid(v.id) ? v.id : null,
      };
    });
  }
  if (patch.translations !== undefined)
    apiPatch.Translations = patch.translations;

  return apiPatch;
}

async function fetchRemoteList(params: ListParams): Promise<ListResponse> {
  let lastError: unknown = null;
  const anyAdmin: any = admin as any;
  if (typeof anyAdmin.listProducts === "function") {
    try {
      const res = await anyAdmin.listProducts(params);
      if (res && Array.isArray(res.items)) {
        return {
          items: res.items.map((r: any) => toRow(normalizeProduct(r))),
          total: Number(res.total ?? res.items.length),
          page: Number(res.page ?? params.page ?? 1),
          pageSize: Number(res.pageSize ?? params.pageSize ?? 20),
        };
      }
    } catch (err) {
      lastError = err;
    }
  }

  try {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qp.set(k, String(v));
    });
    const data = await http.get<any>(`/api/admin/products?${qp.toString()}`);
    if (data && Array.isArray(data.items)) {
      return {
        items: data.items.map((r: any) => toRow(normalizeProduct(r))),
        total: Number(data.total ?? data.items.length),
        page: Number(data.page ?? params.page ?? 1),
        pageSize: Number(data.pageSize ?? params.pageSize ?? 20),
      };
    }
    throw new Error(
      translateUi("malformedProductsResponse", "Malformed products response"),
    );
  } catch (err) {
    throw (
      err ??
      lastError ??
      new Error(translateUi("failedToLoadProducts", "Failed to load products"))
    );
  }
}

export async function listProducts(
  params: ListParams = {},
): Promise<ListResponse> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  if (stubMode()) return listProductsFromStub(params, page, pageSize);
  try {
    return await fetchRemoteList({ ...params, page, pageSize });
  } catch (err) {
    if (stubMode()) return listProductsFromStub(params, page, pageSize);
    throw err instanceof Error
      ? err
      : new Error(
          translateUi("failedToLoadProducts", "Failed to load products"),
        );
  }
}

export async function getProduct(id: string): Promise<ProductDetail | null> {
  let lastError: unknown = null;
  try {
    const anyAdmin: any = admin as any;
    if (typeof anyAdmin.getProduct === "function") {
      const res = await anyAdmin.getProduct(id);
      if (res) return normalizeProduct(res);
    }
  } catch (err) {
    lastError = err;
  }

  if (stubMode()) {
    const local = loadLocal().find((p) => p.id === id);
    if (local) return { ...local };
    return null;
  }

  try {
    const data = await http.get<any>(`/api/admin/products/${id}`);
    if (data) return normalizeProduct(data);
    return null;
  } catch (err) {
    if (parseApiError(err).status === 404) return null;
    lastError = err ?? lastError;
  }
  if (lastError instanceof Error) throw lastError;
  throw new Error(translateUi("failedToLoadProduct", "Failed to load product"));
}

export async function getProductWithRetry(
  id: string,
  options: {
    attempts?: number;
    retryNotFound?: boolean;
    delayMs?: number;
  } = {},
): Promise<ProductDetail | null> {
  const attempts = Math.max(1, options.attempts ?? PRODUCT_LOAD_RETRY_ATTEMPTS);
  const delayMs = options.delayMs ?? 350;
  let lastError: unknown = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const product = await getProduct(id);
      if (product || !options.retryNotFound || attempt === attempts - 1) {
        return product;
      }
    } catch (err) {
      lastError = err;
      if (!isRetryableLoadError(err) || attempt === attempts - 1) {
        throw err;
      }
    }

    await delay(delayMs * (attempt + 1));
  }

  if (lastError instanceof Error) throw lastError;
  return null;
}

export async function translateProductDirect(
  id: string,
  sourceLanguage: string,
  targetLanguages: string[],
): Promise<ProductTranslationResult> {
  if (!id)
    throw new Error(
      translateUi(
        "productTranslationRequiresAProductId",
        "Product translation requires a product id",
      ),
    );
  if (!sourceLanguage)
    throw new Error(
      translateUi("selectASourceLanguage", "Select a source language"),
    );
  if (targetLanguages.length === 0)
    throw new Error(
      translateUi(
        "selectAtLeastOneTargetLanguage",
        "Select at least one target language",
      ),
    );

  return await http.post<ProductTranslationResult>(
    `/api/admin/products/${id}/translate`,
    { sourceLanguage, targetLanguages },
  );
}

export async function saveProductTranslationsDirect(
  id: string,
  defaultLanguage: string,
  translations: Record<string, ProductTranslation>,
): Promise<ProductDetail> {
  if (!id)
    throw new Error(
      translateUi(
        "productTranslationRequiresAProductId",
        "Product translation requires a product id",
      ),
    );
  if (!defaultLanguage)
    throw new Error(
      translateUi("selectADefaultLanguage", "Select a default language"),
    );

  if (stubMode()) {
    const list = loadLocal();
    let updated: ProductDetail | null = null;
    const normalizedDefault = defaultLanguage
      .trim()
      .replace("_", "-")
      .toLowerCase()
      .split("-")[0];
    const defaultFields = Object.entries(translations).find(
      ([language]) =>
        language.trim().replace("_", "-").toLowerCase().split("-")[0] ===
        normalizedDefault,
    )?.[1];
    const next = list.map((product) => {
      if (product.id !== id) return product;
      updated = normalizeProduct({
        ...product,
        name: defaultFields?.title || product.name,
        description: defaultFields?.description || "",
        shortDescription: defaultFields?.shortDescription,
        metaTitle: defaultFields?.metaTitle,
        metaDescription: defaultFields?.metaDescription,
        translations,
        updatedAt: new Date().toISOString(),
      });
      return updated;
    });
    if (!updated)
      throw new Error(
        translateUi("productNotFoundInStub", "Product not found in stub"),
      );
    persistLocal(next);
    return updated;
  }

  const result = await http.put(`/api/admin/products/${id}/translations`, {
    defaultLanguage,
    translations,
  });
  if (result) return normalizeProduct(result);
  throw new Error(
    translateUi(
      "failedToSaveProductTranslations",
      "Failed to save product translations",
    ),
  );
}

export async function estimateProductTranslations(
  sourceLanguage: string,
  targetLanguages: string[],
): Promise<ProductTranslationEstimate> {
  return await http.post<ProductTranslationEstimate>(
    "/api/admin/products/translations/estimate",
    { sourceLanguage, targetLanguages },
  );
}

export async function startProductTranslationJob(input: {
  mode: "full" | "missing";
  sourceLanguage: string;
  targetLanguages: string[];
  confirmOverwrite?: boolean;
}): Promise<ProductTranslationJob> {
  return await http.post<ProductTranslationJob>(
    "/api/admin/products/translations/jobs",
    input,
  );
}

export async function getActiveProductTranslationJob(): Promise<ProductTranslationJob | null> {
  return (
    (await http.get<ProductTranslationJob | null>(
      "/api/admin/products/translations/jobs/active",
    )) || null
  );
}

export async function getProductTranslationJob(
  jobId: string,
): Promise<ProductTranslationJob> {
  return await http.get<ProductTranslationJob>(
    `/api/admin/products/translations/jobs/${jobId}`,
  );
}

export async function cancelProductTranslationJob(
  jobId: string,
): Promise<void> {
  await http.post(`/api/admin/products/translations/jobs/${jobId}/cancel`, {});
}

export async function createProductPreviewToken(
  id: string,
): Promise<{ token: string }> {
  if (!id)
    throw new Error(
      translateUi(
        "productPreviewRequiresAProductId",
        "Product preview requires a product id",
      ),
    );
  const res = await http.post<{ token: string }>(
    `/api/admin/products/${id}/preview`,
    {},
  );
  if (!res?.token)
    throw new Error(
      translateUi(
        "failedToCreateProductPreviewToken",
        "Failed to create product preview token",
      ),
    );
  return res;
}

export async function createProduct(
  input: ProductInput,
  idempotencyKey?: string,
): Promise<ProductDetail> {
  const payload = {
    ...input,
    name: sanitizeString(input.name),
    description: sanitizeString(input.description),
    slug: sanitizeString(input.slug),
    metaTitle: sanitizeString(input.metaTitle),
    metaDescription: sanitizeString(input.metaDescription),
    status: input.status ?? "active",
  };
  assertProductNumericPayload(payload, {
    requirePrice: true,
    requireStock: payload.trackInventory !== false,
  });
  if (stubMode()) {
    const now = new Date().toISOString();
    const list = loadLocal();
    const firstPosition = list.length
      ? Math.min(...list.map((p) => p.storefrontSortOrder ?? 0))
      : 0;
    const product: ProductDetail = normalizeProduct({
      ...payload,
      storefrontSortOrder: firstPosition - 1024,
      status: payload.activateAtUtc ? "inactive" : payload.status,
      // Fix: use title instead of undefined name (Using name now as per type)
      name: payload.name,
      id: ensureId("prod"),
      createdAt: now,
      updatedAt: now,
    });
    persistLocal(list.concat([product]));
    return product;
  }
  try {
    // Map to PascalCase DTO for ASP.NET API
    const apiPayload = {
      // Id: omitted for create
      ActivateAtUtc: payload.activateAtUtc || null,
      Title: payload.name, // Fixed: Name -> Title
      Description: payload.description || "",
      ShortDescription: payload.shortDescription || "",
      Slug: payload.slug,
      Sku: payload.sku,
      Price: payload.price,
      SalePrice: payload.salePrice,
      DefaultCurrency: payload.currency || "USD",
      IsActive: payload.status !== "inactive",
      TrackInventory: payload.trackInventory ?? true,
      StockQuantity: payload.stockQty ?? 0,
      BrandId: payload.brandId,
      CategoryIds: payload.categoryIds || [],
      LabelIds: payload.labelIds || [],
      MetaTitle: payload.metaTitle,
      MetaDescription: payload.metaDescription,
      Weight: payload.weight,
      Images: (payload.images || []).map((img, idx) => mapImageToApi(img, idx)),
      Variants: (payload.variants || []).map((v) => {
        const stockQuantity = v.stockQty ?? v.stockQuantity ?? 0;
        return {
          Sku: v.sku,
          Barcode: v.barcode,
          Price: v.price,
          Weight: v.weight,
          CompareAtPrice: v.compareAt,
          InheritsBasePrice: v.inheritsBasePrice ?? false,
          Currency: v.currency || payload.currency || "USD",
          StockQuantity: stockQuantity,
          stockQuantity,
          TrackInventory: v.trackInventory ?? true,
          IsDefault: v.isDefault ?? false,
          IsActive: v.isActive ?? true,
          SortOrder: v.sortOrder ?? 0,
          Options: mapVariantOptions(v.options),
          TierPrices: v.tierPrices || {},
          // Only send ID if it's a valid GUID, otherwise null for backend to auto-generate
          Id: isValidGuid(v.id) ? v.id : null,
        };
      }),
      Translations: payload.translations || {},
    };

    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers["Idempotency-Key"] = idempotencyKey;
    }

    const res = await http.post<any>("/api/admin/products", apiPayload, {
      headers,
    });
    if (res) {
      const createdProductId = rawProductId(res);
      if (!createdProductId) {
        throw new Error(
          translateUi(
            "productCreationWasNotConfirmedByTheServer",
            "Product creation was not confirmed by the server. Your form data is still here; retry saving.",
          ),
        );
      }
      if (!hasCompleteProductCreatePayload(res)) {
        const confirmed = await getProductWithRetry(createdProductId, {
          retryNotFound: true,
        });
        if (!confirmed) {
          throw new Error(
            translateUi(
              "productSaveCompletedButTheSavedProductCould",
              "Product save completed, but the saved product could not be loaded.",
            ),
          );
        }
        return confirmed;
      }
      const created = normalizeProduct(res);
      return created;
    }
  } catch (err) {
    if (!stubMode())
      throw err instanceof Error
        ? err
        : new Error(
            translateUi("failedToCreateProduct", "Failed to create product"),
          );
  }
  throw new Error(
    translateUi("failedToCreateProduct", "Failed to create product"),
  );
}

export async function updateProduct(
  id: string,
  patch: Partial<ProductInput>,
): Promise<ProductDetail> {
  const now = new Date().toISOString();
  assertProductNumericPayload(patch);
  if (stubMode()) {
    const list = loadLocal();
    let updated: ProductDetail | null = null;
    const next = list.map((p) => {
      if (p.id !== id) return p;
      updated = normalizeProduct({
        ...p,
        ...patch,
        status: patch.activateAtUtc ? "inactive" : (patch.status ?? p.status),
        activateAtUtc:
          patch.activateAtUtc !== undefined
            ? patch.activateAtUtc
            : patch.status !== undefined
              ? null
              : p.activateAtUtc,
        updatedAt: now,
      } as any);
      return updated;
    });
    if (updated) persistLocal(next);
    if (!updated)
      throw new Error(
        translateUi("productNotFoundInStub", "Product not found in stub"),
      );
    return updated;
  }

  try {
    // const anyAdmin: any = admin as any;
    // if (typeof anyAdmin.updateProduct === 'function') {
    //   const res = await anyAdmin.updateProduct(id, patch);
    //   if (res) return normalizeProduct(res);
    // }

    const apiPatch = toApiProductMutationPayload(patch);

    const res = await http.patch(`/api/admin/products/${id}`, apiPatch);
    if (res) return normalizeProduct(res);
  } catch (err) {
    if (!stubMode())
      throw err instanceof Error
        ? err
        : new Error(
            translateUi("failedToUpdateProduct", "Failed to update product"),
          );
  }

  throw new Error(
    translateUi("failedToUpdateProduct", "Failed to update product"),
  );
}

export async function deleteProducts(ids: string[]): Promise<void> {
  if (stubMode()) {
    const list = loadLocal().filter((p) => !ids.includes(p.id));
    persistLocal(list);
    return;
  }
  try {
    const results = await Promise.allSettled(
      ids.map((id) => http.delete?.(`/api/admin/products/${id}`)),
    );
    const failed = results.find((result) => result.status === "rejected");
    if (failed?.status === "rejected") throw failed.reason;
    return;
  } catch (err) {
    if (!stubMode())
      throw err instanceof Error
        ? err
        : new Error(
            translateUi("failedToDeleteProducts", "Failed to delete products"),
          );
  }
  throw new Error(
    translateUi("failedToDeleteProducts", "Failed to delete products"),
  );
}

export async function bulkUpdateStatus(
  ids: string[],
  status: ProductStatus,
): Promise<BulkEditProductsResult> {
  const { bulkVisibility } = await import("./productMerchandising");
  return bulkVisibility(ids, status === "active" ? "show" : "hide");
}

export async function bulkAssignCategories(
  productIds: string[],
  categoryIds: string[],
): Promise<{ updatedCount: number }> {
  if (stubMode()) {
    // Stub implementation: update local storage
    const list = loadLocal().map((p) => {
      if (productIds.includes(p.id)) {
        const current = new Set(p.categoryIds);
        categoryIds.forEach((c) => current.add(c));
        return {
          ...p,
          categoryIds: Array.from(current),
          updatedAt: new Date().toISOString(),
        };
      }
      return p;
    });
    persistLocal(list);
    return { updatedCount: productIds.length };
  }
  return await http.post("/api/admin/products/bulk-assign-categories", {
    productIds,
    categoryIds,
  });
}

function normalizeBulkEditSnapshot(raw: any): BulkEditProductSnapshot {
  return {
    id: String(raw?.id ?? raw?.Id ?? ""),
    title: String(raw?.title ?? raw?.Title ?? "Untitled product"),
    status: String(raw?.status ?? raw?.Status ?? "active") as ProductStatus,
    isVariable: Boolean(raw?.isVariable ?? raw?.IsVariable),
    price: Number(raw?.price ?? raw?.Price ?? 0),
    salePrice: raw?.salePrice ?? raw?.SalePrice ?? null,
    stockQuantity: Number(raw?.stockQuantity ?? raw?.StockQuantity ?? 0),
    trackInventory: Boolean(raw?.trackInventory ?? raw?.TrackInventory),
    weight: raw?.weight ?? raw?.Weight ?? null,
    brandId: raw?.brandId ?? raw?.BrandId ?? null,
    categoryIds: (raw?.categoryIds ?? raw?.CategoryIds ?? []).map(String),
    labelIds: (raw?.labelIds ?? raw?.LabelIds ?? []).map(String),
    rowVersion: raw?.rowVersion ?? raw?.RowVersion ?? null,
    activateAtUtc: raw?.activateAtUtc ?? raw?.ActivateAtUtc ?? null,
  };
}

export async function loadBulkEditProducts(
  productIds: string[],
): Promise<BulkEditSnapshotResponse> {
  const uniqueIds = Array.from(new Set(productIds.filter(Boolean)));
  if (uniqueIds.length === 0) return { items: [], missingProductIds: [] };

  if (stubMode()) {
    const byId = new Map(loadLocal().map((product) => [product.id, product]));
    const items: BulkEditProductSnapshot[] = [];
    const missingProductIds: string[] = [];
    uniqueIds.forEach((id) => {
      const product = byId.get(id);
      if (!product) {
        missingProductIds.push(id);
        return;
      }
      items.push({
        id: product.id,
        title: product.name,
        status: product.status,
        isVariable: product.productType === 1,
        price: product.price,
        salePrice: product.salePrice ?? null,
        stockQuantity: product.stockQty,
        trackInventory: product.trackInventory,
        weight: product.weight ?? null,
        brandId: product.brandId ?? null,
        categoryIds: product.categoryIds || [],
        labelIds: product.labelIds || [],
        rowVersion: null,
        activateAtUtc: product.activateAtUtc ?? null,
      });
    });
    return { items, missingProductIds };
  }

  const response = await http.post<any>(
    "/api/admin/products/bulk-edit/snapshot",
    { productIds: uniqueIds },
  );
  return {
    items: (response?.items ?? response?.Items ?? []).map(
      normalizeBulkEditSnapshot,
    ),
    missingProductIds: (
      response?.missingProductIds ??
      response?.MissingProductIds ??
      []
    ).map(String),
  };
}

export async function bulkEditProducts(
  items: BulkEditProductPatch[],
): Promise<BulkEditProductsResult> {
  if (items.length === 0) {
    return {
      requestedCount: 0,
      updatedCount: 0,
      failedCount: 0,
      skippedCount: 0,
      items: [],
    };
  }

  if (stubMode()) {
    const patches = new Map(items.map((item) => [item.productId, item]));
    const results: BulkEditProductResultItem[] = [];
    const now = new Date().toISOString();
    const next = loadLocal().map((product) => {
      const patch = patches.get(product.id);
      if (!patch) return product;
      const changed = new Set(patch.changedFields);
      if (
        product.productType === 1 &&
        patch.changedFields.some((field) =>
          [
            "price",
            "salePrice",
            "stockQuantity",
            "trackInventory",
            "weight",
          ].includes(field),
        )
      ) {
        results.push({
          productId: product.id,
          outcome: "failed",
          code: "variant_edit_required",
          message: translateUi(
            "pricingAndInventoryForProductsWithVariantsMust",
            "Pricing and inventory for products with variants must be edited in the product editor.",
          ),
          status: product.status,
        });
        return product;
      }
      const updated = normalizeProduct({
        ...product,
        price: changed.has("price") ? patch.price : product.price,
        salePrice: changed.has("salePrice")
          ? patch.salePrice
          : product.salePrice,
        stockQty: changed.has("stockQuantity")
          ? patch.stockQuantity
          : product.stockQty,
        trackInventory: changed.has("trackInventory")
          ? patch.trackInventory
          : product.trackInventory,
        weight: changed.has("weight") ? patch.weight : product.weight,
        brandId: changed.has("brandId") ? patch.brandId : product.brandId,
        categoryIds: changed.has("categoryIds")
          ? patch.categoryIds || []
          : product.categoryIds,
        labelIds: changed.has("labelIds")
          ? patch.labelIds || []
          : product.labelIds,
        status:
          changed.has("activateAtUtc") && patch.activateAtUtc
            ? "inactive"
            : changed.has("status")
              ? patch.status
              : product.status,
        activateAtUtc: changed.has("activateAtUtc")
          ? patch.activateAtUtc
          : changed.has("status")
            ? null
            : product.activateAtUtc,
        updatedAt: now,
      });
      results.push({
        productId: product.id,
        outcome: "updated",
        status: updated.status,
      });
      return updated;
    });
    persistLocal(next);
    items.forEach((patch) => {
      if (!next.some((product) => product.id === patch.productId)) {
        results.push({
          productId: patch.productId,
          outcome: "failed",
          code: "not_found",
          message: translateUi("productWasNotFound", "Product was not found."),
        });
      }
    });
    const updatedCount = results.filter(
      (item) => item.outcome === "updated",
    ).length;
    const failedCount = results.filter(
      (item) => item.outcome === "failed",
    ).length;
    return {
      requestedCount: items.length,
      updatedCount,
      failedCount,
      skippedCount: 0,
      items: results,
    };
  }

  return await http.patch<BulkEditProductsResult>(
    "/api/admin/products/bulk-edit",
    { items },
    {
      headers: {
        "Idempotency-Key": `product-bulk-edit-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      },
    },
  );
}

export type ImportRow = {
  id?: string | null;
  title: string;
  slug?: string;
  description?: string;
  price: number;
  compareAtPrice?: number;
  status: ProductStatus;
  activateAtUtc?: string;
  sku?: string;
  barcode?: string;
  currency?: string;
  stockQty?: number;
  trackInventory?: boolean;
  weight?: number;
  brandId?: string;
  brand?: string;
  categoryIds?: string;
  categories?: string;
  images?: string;
  clearImages?: boolean;
  translations?: Record<string, ProductTranslation>;
};

export type ProductImportError = {
  rowNumber: number;
  title?: string | null;
  slug?: string | null;
  errors: string[];
  warnings?: string[];
  imageCount?: number;
};

export type ProductImportResponse = {
  total: number;
  imported: number;
  failed: number;
  created: number;
  updated: number;
  imagesRequested: number;
  imagesImported: number;
  imagesFailed: number;
  errors: ProductImportError[];
};

export async function importProducts(
  rows: ImportRow[],
): Promise<ProductImportResponse> {
  if (!stubMode()) {
    const translationColumns = CATALOG_TRANSLATION_LOCALES.flatMap((locale) =>
      PRODUCT_TRANSLATION_FIELDS.filter((field) =>
        rows.some((row) =>
          Object.prototype.hasOwnProperty.call(
            row.translations?.[locale] ?? {},
            field,
          ),
        ),
      ).map((field) => productTranslationColumn(locale, field)),
    );
    const payloadRows = rows.map((r) => ({
      id: r.id ?? "",
      title: r.title,
      slug: r.slug ?? "",
      description: r.description ?? "",
      price: r.price,
      compareAtPrice:
        typeof r.compareAtPrice === "number" ? r.compareAtPrice : "",
      status: r.status,
      activateAtUtc: r.activateAtUtc ?? "",
      sku: r.sku ?? "",
      barcode: r.barcode ?? "",
      currency: r.currency ?? "",
      stockQty: typeof r.stockQty === "number" ? r.stockQty : "",
      trackInventory:
        typeof r.trackInventory === "boolean" ? r.trackInventory : "",
      weight: typeof r.weight === "number" ? r.weight : "",
      brandId: r.brandId ?? "",
      brand: r.brand ?? "",
      categoryIds: r.categoryIds ?? "",
      categories: r.categories ?? "",
      images: r.images ?? "",
      clearImages: typeof r.clearImages === "boolean" ? r.clearImages : "",
      ...Object.fromEntries(
        translationColumns.map((column) => {
          const [, locale, field] = column.split(".");
          return [
            column,
            (
              r.translations?.[locale] as
                | Record<string, string | undefined>
                | undefined
            )?.[field] ?? "",
          ];
        }),
      ),
    }));
    const csv = toCSV(
      [
        "id",
        "title",
        "slug",
        "description",
        "price",
        "compareAtPrice",
        "status",
        "activateAtUtc",
        "sku",
        "barcode",
        "currency",
        "stockQty",
        "trackInventory",
        "weight",
        "brandId",
        "brand",
        "categoryIds",
        "categories",
        "images",
        "clearImages",
        ...translationColumns,
      ],
      payloadRows as any,
    );
    const fd = new FormData();
    fd.append(
      "file",
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      "products.csv",
    );
    try {
      const res = await http.post<any>(
        "/api/admin/products/import?preview=false&format=csv",
        fd as any,
        { timeoutMs: 30 * 60 * 1000 },
      );
      const items = Array.isArray(res?.items) ? res.items : [];
      const errors = items
        .map((it: any) => {
          const rowErrors = Array.isArray(it?.errors)
            ? it.errors
            : Array.isArray(it?.Errors)
              ? it.Errors
              : [];
          return {
            rowNumber: Number(it?.rowNumber ?? it?.RowNumber ?? 0),
            title: it?.title ?? it?.Title ?? null,
            slug: it?.slug ?? it?.Slug ?? null,
            errors: rowErrors,
            warnings: Array.isArray(it?.warnings)
              ? it.warnings
              : Array.isArray(it?.Warnings)
                ? it.Warnings
                : [],
            imageCount: Number(it?.imageCount ?? it?.ImageCount ?? 0),
          };
        })
        .filter((it: ProductImportError) => it.errors.length > 0);
      const imported = Number(res?.imported ?? res?.Imported ?? 0);
      const failed = Number(res?.failed ?? res?.Failed ?? errors.length);
      const created = Number(
        res?.created ??
          res?.Created ??
          (imported > 0 && failed === 0
            ? rows.filter((r) => !r.id).length
            : imported),
      );
      const updated = Number(
        res?.updated ??
          res?.Updated ??
          (imported > 0 && failed === 0 ? rows.filter((r) => r.id).length : 0),
      );
      return {
        total: Number(res?.total ?? res?.Total ?? rows.length),
        imported,
        failed,
        created,
        updated,
        imagesRequested: Number(
          res?.imagesRequested ?? res?.ImagesRequested ?? 0,
        ),
        imagesImported: Number(res?.imagesImported ?? res?.ImagesImported ?? 0),
        imagesFailed: Number(res?.imagesFailed ?? res?.ImagesFailed ?? 0),
        errors,
      };
    } catch (err: any) {
      throw err instanceof Error
        ? err
        : new Error(
            err?.message || translateUi("importFailed", "Import failed"),
          );
    }
  }

  const errors: ProductImportError[] = [];
  rows.forEach((r, idx) => {
    const rowErrors: string[] = [];
    if (!r.title)
      rowErrors.push(translateUi("titleRequired", "Title required"));
    if (r.slug && !SLUG_REGEX.test(r.slug))
      rowErrors.push(translateUi("slugInvalid", "Slug invalid"));
    if (!Number.isFinite(r.price) || r.price < 0)
      rowErrors.push(translateUi("priceInvalid", "Price invalid"));
    else if (!hasUpToDecimalPlaces(r.price, 2))
      rowErrors.push(
        translateUi(
          "priceMustUseUpTo2DecimalPlaces",
          "Price must use up to 2 decimal places",
        ),
      );
    if (typeof r.compareAtPrice !== "undefined") {
      if (!Number.isFinite(r.compareAtPrice) || r.compareAtPrice < 0)
        rowErrors.push(translateUi("salePriceInvalid", "Sale price invalid"));
      else if (!hasUpToDecimalPlaces(r.compareAtPrice, 2))
        rowErrors.push(
          translateUi(
            "salePriceMustUseUpTo2Decimal",
            "Sale price must use up to 2 decimal places",
          ),
        );
      else if (Number.isFinite(r.price) && r.compareAtPrice > r.price)
        rowErrors.push(
          translateUi(
            "salePriceMustBeLessThanOrEqualToPrice",
            "Sale price must be less than or equal to price",
          ),
        );
    }
    if (!["active", "inactive"].includes(r.status))
      rowErrors.push(translateUi("invalidStatus", "Invalid status"));
    if (r.currency && r.currency.length !== 3)
      rowErrors.push(translateUi("currencyInvalid", "Currency invalid"));
    if (
      typeof r.stockQty !== "undefined" &&
      (!isWholeNumber(r.stockQty) || r.stockQty < 0)
    )
      rowErrors.push(
        translateUi("stockQuantityInvalid", "Stock quantity invalid"),
      );
    if (
      typeof r.trackInventory !== "undefined" &&
      typeof r.trackInventory !== "boolean"
    )
      rowErrors.push(
        translateUi("trackInventoryInvalid", "Track inventory invalid"),
      );
    if (
      typeof r.weight !== "undefined" &&
      (!Number.isFinite(r.weight) || r.weight < 0)
    )
      rowErrors.push(translateUi("weightInvalid", "Weight invalid"));
    if (rowErrors.length) {
      errors.push({
        rowNumber: idx + 1,
        title: r.title,
        slug: r.slug ?? null,
        errors: rowErrors,
      });
    }
  });
  if (errors.length) {
    return {
      total: rows.length,
      imported: 0,
      failed: errors.length,
      created: 0,
      updated: 0,
      imagesRequested: rows.reduce(
        (total, row) =>
          total + (row.images?.split(";").filter(Boolean).length ?? 0),
        0,
      ),
      imagesImported: 0,
      imagesFailed: 0,
      errors,
    };
  }

  const next = loadLocal();
  let created = 0;
  let updated = 0;
  rows.forEach((r) => {
    const existingIdx = r.id ? next.findIndex((p) => p.id === r.id) : -1;
    const now = new Date().toISOString();
    const detail = normalizeProduct({
      id: r.id ?? ensureId("prod"),
      name: r.title,
      description: r.description,
      slug: r.slug,
      price: r.price,
      compareAtPrice: r.compareAtPrice,
      sku: r.sku ?? "",
      currency: r.currency ?? "USD",
      trackInventory: r.trackInventory ?? true,
      stockQty: typeof r.stockQty === "number" ? r.stockQty : 0,
      status: r.status,
      brandId: r.brandId,
      brandName: r.brand,
      categoryIds: r.categoryIds
        ? r.categoryIds
            .split(";")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
      weight: r.weight,
      images: r.images
        ? r.images
            .split(";")
            .map((url, index) => ({
              id: `import-image-${index}`,
              url: url.trim(),
              sort: index,
              isPrimary: index === 0,
            }))
            .filter((image) => image.url)
        : undefined,
      translations: r.translations,
      createdAt: now,
      updatedAt: now,
    });
    if (existingIdx >= 0) {
      next[existingIdx] = {
        ...next[existingIdx],
        ...detail,
        createdAt: next[existingIdx].createdAt,
        updatedAt: now,
      };
      updated += 1;
    } else {
      next.push(detail);
      created += 1;
    }
  });
  if (created + updated > 0) persistLocal(next);
  return {
    total: rows.length,
    imported: created + updated,
    failed: errors.length,
    created,
    updated,
    imagesRequested: rows.reduce(
      (total, row) =>
        total + (row.images?.split(";").filter(Boolean).length ?? 0),
      0,
    ),
    imagesImported: rows.reduce(
      (total, row) =>
        total + (row.images?.split(";").filter(Boolean).length ?? 0),
      0,
    ),
    imagesFailed: 0,
    errors,
  };
}

export async function exportProducts(
  filters: ListParams = {},
): Promise<string> {
  const { items } = await listProducts({ ...filters, page: 1, pageSize: 1000 });
  const translationColumns = CATALOG_TRANSLATION_LOCALES.flatMap((locale) =>
    PRODUCT_TRANSLATION_FIELDS.map((field) =>
      productTranslationColumn(locale, field),
    ),
  );
  return toCSV(
    [
      "id",
      "title",
      "slug",
      "description",
      "price",
      "compareAtPrice",
      "status",
      "sku",
      "barcode",
      "currency",
      "stockQty",
      "trackInventory",
      "weight",
      "brandId",
      "brand",
      "categoryIds",
      "categories",
      "images",
      ...translationColumns,
    ],
    items.map((p) => ({
      id: p.id,
      title: p.name,
      slug: p.slug || "",
      description: "",
      price: p.priceMin,
      compareAtPrice: p.compareAt ?? "",
      status: p.status,
      sku: p.sku || "",
      barcode: "",
      currency: p.currency || "",
      stockQty: typeof p.stockQty === "number" ? p.stockQty : "",
      trackInventory:
        typeof p.trackInventory === "boolean" ? p.trackInventory : "",
      weight: typeof p.weight === "number" ? p.weight : "",
      brandId: p.brandId || "",
      brand: p.brandName || "",
      categoryIds: (p.categoryIds || []).join("; "),
      categories: "",
      images: p.imageUrl || "",
      ...Object.fromEntries(
        translationColumns.map((column) => {
          const [, locale, field] = column.split(".");
          return [
            column,
            (
              p.translations?.[locale] as
                | Record<string, string | undefined>
                | undefined
            )?.[field] ?? "",
          ];
        }),
      ),
    })),
  );
}

export async function downloadProductsExport(
  format: "csv" | "json" = "csv",
): Promise<{ blob: Blob; fileName: string }> {
  if (stubMode()) {
    if (format === "json") {
      const { items } = await listProducts({ page: 1, pageSize: 1000 });
      const blob = new Blob([JSON.stringify(items, null, 2)], {
        type: "application/json;charset=utf-8;",
      });
      return { blob, fileName: "products.json" };
    }
    const csv = await exportProducts();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    return { blob, fileName: "products.csv" };
  }

  const token = getAccessToken();
  const tenantId = getTenantId();
  const url = makeApiUrl(
    `/api/admin/products/export?format=${encodeURIComponent(format)}`,
  );
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantId) {
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        tenantId,
      );
    headers[isUuid ? "X-Tenant-Id" : "X-Store-Slug"] = tenantId;
  }

  const res = await fetch(url, { headers });
  if (!res.ok)
    throw new Error(
      translateUi("failedToDownloadExport", "Failed to download export"),
    );

  const blob = await res.blob();
  const fileName = parseDownloadFileName(
    res.headers.get("Content-Disposition"),
    `products.${format}`,
  );
  return { blob, fileName };
}
